import { activateDrawer, lockScroll, unlockScroll, bindDrawerEscape } from '@theme/coco-utilities';
import { onCocoEvent, COCO_EVENTS } from '@theme/theme-events';

/**
 * Store pickup availability.
 * Custom elements: coco-pickup, coco-pickup-drawer
 * Section Rendering: section_id=coco-pickup
 */

const CE_PICKUP = 'coco-pickup';
const CE_DRAWER = 'coco-pickup-drawer';
const SECTION_ID = 'coco-pickup';

function getPickupDrawer(drawerId) {
  return document.getElementById(drawerId);
}

class CocoPickupDrawer extends HTMLElement {
  #activeElement = null;
  #releaseModal = null;
  #onClick = null;
  #unbindEscape = null;

  connectedCallback() {
    this.#onClick = (event) => this.#handleClick(event);

    this.addEventListener('click', this.#onClick);
    this.#unbindEscape = bindDrawerEscape(this, () => this.close());
    this.#moveToBody();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#unbindEscape?.();
    this.#unbindEscape = null;
    this.#releaseModal?.();
    this.#releaseModal = null;
  }

  #moveToBody() {
    if (this.parentElement === document.body) return;

    const existing = this.id ? document.getElementById(this.id) : null;
    if (existing && existing !== this) existing.remove();

    document.body.appendChild(this);
  }

  #handleClick(event) { 
    if (!this.hasAttribute('active')) return;

    const inner = this.querySelector('[data-drawer-inner]');
    const isInsidePanel = inner?.contains(event.target);

    if (event.target.closest('[data-pickup-drawer-close], [is="close-drawer"]')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (!isInsidePanel) {
      event.preventDefault();
      this.close();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.#activeElement = triggeredBy;

    // Nested under quick-view focus trap: drawer may have been marked inert on body.
    this.removeAttribute('inert');

    this.setAttribute('open', '');
    requestAnimationFrame(() => this.setAttribute('active', ''));
    lockScroll();

    if (triggeredBy) triggeredBy.setAttribute('aria-expanded', 'true');

    this.#releaseModal?.();
    this.#releaseModal = activateDrawer(this, {
      initialFocus: (drawer) =>
        drawer.querySelector(
          '[data-pickup-drawer-close]:not([data-fixed-overlay]), [is="close-drawer"]:not([data-fixed-overlay])',
        ),
    });
  }

  close() {
    if (!this.hasAttribute('active') && !this.hasAttribute('open')) return;

    this.removeAttribute('active');

    const trigger = this.#activeElement?.isConnected
      ? this.#activeElement
      : document.querySelector(`[data-pickup-drawer-open][aria-controls="${this.id}"]`);
    trigger?.setAttribute?.('aria-expanded', 'false');

    this.#releaseModal?.(trigger);
    this.#releaseModal = null;
    this.#activeElement = null;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      this.removeAttribute('open');
      unlockScroll();
    };

    const inner = this.querySelector('[data-drawer-inner]');
    if (inner) {
      const onTransitionEnd = (event) => {
        if (event.target !== inner || event.propertyName !== 'transform') return;
        finish();
      };
      inner.addEventListener('transitionend', onTransitionEnd);
    }

    setTimeout(finish, 650);
  }
}

class CocoPickup extends HTMLElement {
  #sectionId = '';
  #productId = '';
  #drawerId = '';
  #errorTemplate = null;
  #unsubscribeVariant = null;
  #onClick = null;

  connectedCallback() {
    this.#sectionId = this.dataset.sectionId;
    this.#productId = this.dataset.productId;
    this.#drawerId = this.dataset.drawerId;
    this.#errorTemplate = this.querySelector('[data-pickup-error-template]');

    this.#onClick = (event) => this.#handleClick(event);
    this.addEventListener('click', this.#onClick);
    this.#unsubscribeVariant = onCocoEvent(COCO_EVENTS.variantChanged, (payload) =>
      this.#onVariantChange(payload),
    );
    this.#bindDrawerTriggers();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#unsubscribeVariant?.();

    // Drawer is moved to <body>, so remove it when pickup block is replaced (e.g. quick view).
    const drawer = getPickupDrawer(this.#drawerId);
    if (!drawer) return;

    if (drawer.hasAttribute('active') || drawer.hasAttribute('open')) {
      drawer.close?.();
    }
    drawer.remove();
  }

  #onVariantChange(payload) {
    if (payload?.sectionId && String(payload.sectionId) !== String(this.#sectionId)) return;
    if (payload?.productId && String(payload.productId) !== String(this.#productId)) return;

    const variantId = payload?.variant?.id;
    if (variantId) {
      this.dataset.variantId = String(variantId);
      this.#fetchAvailability(variantId);
      return;
    }

    this.#hide();
  }

  #handleClick(event) {
    if (event.target.closest('[data-pickup-refresh]')) {
      event.preventDefault();
      this.#fetchAvailability(this.dataset.variantId);
      return;
    }

    const openTrigger = event.target.closest('[data-pickup-drawer-open]');
    if (!openTrigger) return;

    event.preventDefault();
    getPickupDrawer(this.#drawerId)?.open(openTrigger);
  }

  #bindDrawerTriggers() {
    document.querySelectorAll(`[data-pickup-drawer-open][aria-controls="${this.#drawerId}"]`).forEach((button) => {
      if (button.dataset.pickupDrawerBound) return;
      button.dataset.pickupDrawerBound = 'true';
      button.setAttribute('aria-haspopup', 'dialog');
    });
  }

  get #previewRoot() {
    return this.querySelector('[data-pickup-preview]');
  }

  #fetchAvailability(variantId) {
    if (!variantId) {
      this.#hide();
      return;
    }

    let rootUrl = this.dataset.rootUrl || '/';
    if (!rootUrl.endsWith('/')) rootUrl += '/';

    const url = `${rootUrl}variants/${variantId}/?section_id=${SECTION_ID}`;

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const section = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('[id^="shopify-section-"]');
        if (!section) throw new Error('Pickup section missing');

        const previewSource = section.querySelector('[data-pickup-preview]');
        const drawerBodySource = section.querySelector('[data-pickup-drawer-source]');

        if (!previewSource || previewSource.hasAttribute('hidden')) {
          this.#hide();
          return;
        }

        const previewRoot = this.#previewRoot;
        if (previewRoot) {
          previewRoot.innerHTML = previewSource.innerHTML;
          previewRoot.removeAttribute('hidden');
        }

        this.removeAttribute('hidden');
        this.classList.remove('hidden');

        const drawer = getPickupDrawer(this.#drawerId);
        const drawerBody = drawer?.querySelector('[data-pickup-drawer-body]');
        if (drawerBody && drawerBodySource) {
          drawerBody.innerHTML = drawerBodySource.innerHTML;
        }

        this.#bindDrawerTriggers();
      })
      .catch((error) => {
        console.error(error);
        this.#renderError();
      });
  }

  #renderError() {
    if (!this.#errorTemplate || !this.#previewRoot) return;

    this.#previewRoot.innerHTML = this.#errorTemplate.innerHTML;
    this.removeAttribute('hidden');
    this.classList.remove('hidden');
  }

  #hide() {
    const drawer = getPickupDrawer(this.#drawerId);
    if (drawer?.hasAttribute('active') || drawer?.hasAttribute('open')) drawer.close();

    if (this.#previewRoot) {
      this.#previewRoot.innerHTML = '';
      this.#previewRoot.setAttribute('hidden', '');
    }

    this.setAttribute('hidden', '');
    this.classList.add('hidden');
  }
}

if (!customElements.get(CE_DRAWER)) {
  customElements.define(CE_DRAWER, CocoPickupDrawer);
}

if (!customElements.get(CE_PICKUP)) {
  customElements.define(CE_PICKUP, CocoPickup);
}
